import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { UserCheck, Eye, Phone, Mail, FileText, Search, Download, Briefcase } from 'lucide-react';
import { getUser } from '@/utils/auth';
import api from '@/utils/api';
import { eduDegree, eduInstitution, eduYear, expDesignation, expCompany, expYears, expFromDate, expToDate } from '@/utils/seekerHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExecutiveJobSeekerManagement = () => {
  const user = getUser();
  const [jobSeekers, setJobSeekers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalJobSeekers, setTotalJobSeekers] = useState(0);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedJobSeeker, setSelectedJobSeeker] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    fetchJobSeekers();
  }, [page]);

  const fetchJobSeekers = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/executive/job-seekers?executive_id=${user.id}&page=${page}&limit=${limit}`);
      setJobSeekers(response.data.data || []);
      setTotalJobSeekers(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch job seekers');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (jobSeeker) => {
    try {
      await api.put(`/executive/job-seekers/${jobSeeker.id}/toggle-status?executive_id=${user.id}`);
      toast.success(`Job seeker ${jobSeeker.active ? 'deactivated' : 'activated'} successfully`);
      fetchJobSeekers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const viewJobSeekerDetails = (jobSeeker) => {
    setSelectedJobSeeker(jobSeeker);
    setShowDetailDialog(true);
  };

  const downloadCV = (cvPath) => {
    window.open(`${API_URL}${cvPath}`, '_blank');
  };

  const getStatusBadge = (jobSeeker) => {
    if (jobSeeker.active) {
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge>;
    }
    return <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">Inactive</Badge>;
  };

  const filteredJobSeekers = searchQuery
    ? jobSeekers.filter(js =>
        js.profile?.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        js.mobile?.includes(searchQuery) ||
        js.profile?.email?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : jobSeekers;

  const totalPages = Math.ceil(totalJobSeekers / limit);

  if (loading && page === 1) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>
            Job Seekers
          </h1>
          <p className="text-slate-600">Job seekers who applied to jobs in your districts</p>
        </div>
        <Badge variant="outline" className="px-3 py-1.5">
          <UserCheck className="w-4 h-4 mr-1" /> {totalJobSeekers} Job Seekers
        </Badge>
      </div>

      {/* Search */}
      <Card className="p-4 mb-6 border-slate-200">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Search by name, mobile, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </Card>

      {/* Job Seekers Table */}
      <Card className="border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">Sl.No</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Mobile</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Applications</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Active/Inactive</TableHead>
              <TableHead>CV</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredJobSeekers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12">
                  <UserCheck className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-600">No job seekers found in your allocated districts</p>
                </TableCell>
              </TableRow>
            ) : (
              filteredJobSeekers.map((js, index) => (
                <TableRow key={js.id}>
                  <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                  <TableCell className="font-medium">{js.profile?.full_name || js.name || 'N/A'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Phone className="w-3 h-3 text-slate-400" />
                      {js.mobile}
                    </div>
                  </TableCell>
                  <TableCell>
                    {js.profile?.email ? (
                      <div className="flex items-center gap-1">
                        <Mail className="w-3 h-3 text-slate-400" />
                        {js.profile.email}
                      </div>
                    ) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      <Briefcase className="w-3 h-3 mr-1" />
                      {js.applications_in_district || 0}
                    </Badge>
                  </TableCell>
                  <TableCell>{getStatusBadge(js)}</TableCell>
                  <TableCell>
                    <Switch
                      checked={js.active}
                      onCheckedChange={() => handleToggleStatus(js)}
                    />
                  </TableCell>
                  <TableCell>
                    {js.profile?.cv ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadCV(js.profile.cv)}
                      >
                        <Download className="w-4 h-4 text-blue-600" />
                      </Button>
                    ) : (
                      <span className="text-slate-400 text-sm">No CV</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => viewJobSeekerDetails(js)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-slate-600">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalJobSeekers)} of {totalJobSeekers} job seekers
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Job Seeker Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Job Seeker Details</DialogTitle>
          </DialogHeader>
          {selectedJobSeeker && (
            <div className="space-y-4 mt-4">
              <div className="flex items-start gap-4">
                {selectedJobSeeker.profile?.profile_picture && (
                  <img
                    src={`${API_URL}${selectedJobSeeker.profile.profile_picture}`}
                    alt="Profile"
                    className="w-20 h-20 rounded-full object-cover border"
                  />
                )}
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">
                    {selectedJobSeeker.profile?.full_name || selectedJobSeeker.name || 'N/A'}
                  </h3>
                  <p className="text-slate-600">{selectedJobSeeker.mobile}</p>
                  {selectedJobSeeker.profile?.email && (
                    <p className="text-slate-600">{selectedJobSeeker.profile.email}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-slate-500">Gender</p>
                  <p className="font-medium capitalize">{selectedJobSeeker.profile?.gender || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Date of Birth</p>
                  <p className="font-medium">{selectedJobSeeker.profile?.dob || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">City</p>
                  <p className="font-medium">{selectedJobSeeker.profile?.city || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">State</p>
                  <p className="font-medium">{selectedJobSeeker.profile?.state || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Applications in Your Districts</p>
                  <p className="font-medium">{selectedJobSeeker.applications_in_district || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Profile Strength</p>
                  <p className="font-medium">{selectedJobSeeker.profile?.profile_strength || 0}%</p>
                </div>
              </div>

              {selectedJobSeeker.profile?.education && selectedJobSeeker.profile.education.length > 0 && (
                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-3">Education</h3>
                  <div className="space-y-2">
                    {selectedJobSeeker.profile.education.map((edu, i) => (
                      <div key={i} className="p-3 bg-slate-50 rounded-lg">
                        <p className="font-medium">{eduDegree(edu)}</p>
                        <p className="text-sm text-slate-600">{eduInstitution(edu)} ({eduYear(edu)})</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedJobSeeker.profile?.experience && selectedJobSeeker.profile.experience.length > 0 && (
                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-3">Experience</h3>
                  <div className="space-y-2">
                    {selectedJobSeeker.profile.experience.map((exp, i) => (
                      <div key={i} className="p-3 bg-slate-50 rounded-lg">
                        <p className="font-medium">{expDesignation(exp)}</p>
                        <p className="text-sm text-slate-600">{expCompany(exp)} • {expFromDate(exp)} - {expToDate(exp)} ({expYears(exp)} years)</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedJobSeeker.profile?.cv && (
                <div className="pt-4 border-t">
                  <Button
                    onClick={() => downloadCV(selectedJobSeeker.profile.cv)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download CV
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExecutiveJobSeekerManagement;
